/* eslint-disable jsx-a11y/no-static-element-interactions */
import Button from "@Components/Button";
import useRoomTypeContext from "@Pages/PropertyPage/components/RoomTypeSection/context/useRoomTypeContext";
import {
  formatTenancyDetails,
  getAvailableFrom,
  getLeaseDurationFormatted,
  getAvailableTenancyCount,
  sortTenancies,
  getAvailableTenancy,
  getAvailableTo,
} from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useState, useEffect, useRef } from "react";
import ContactUsSection from "../ContactUsSection/ContactUsSection";
import TenancyCard from "../TenancyCard";
import styles from "./TenanciesSection.module.scss";
import useBookClick from "../useBookClick";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import ArrowDownIcon from "@Icons/ArrowDownIcon";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import throttle from "@Utils/throttle";
import { AppState } from "@Store/index";
import { setExpandedRoomType } from "@Pages/PropertyPage/data/propertyPageReducer";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
//

type TenanciesSection = {
  tenancies: any[];
  roomTypeData?: any;
  isSoldOut: boolean;
  featuredImage: any;
  isInsideModal?: boolean;
  activeTab?: string;
  isDesktop?: boolean;
  setShowMoreDetailsModal?: any;
  nextRoomId?: string;
};

const TenanciesSection = ({
  tenancies,
  roomTypeData,
  isSoldOut = false,
  featuredImage,
  isInsideModal = false,
  activeTab = "",
  isDesktop = false,
  setShowMoreDetailsModal,
  nextRoomId = "",
}: TenanciesSection) => {
  const sortedTenancies = sortTenancies(tenancies);
  const isRoomTypeV2 = true;
  const TENANCIES_TO_SHOW_V1 =
    getAvailableTenancyCount(tenancies) < 4 &&
    tenancies?.length > getAvailableTenancyCount(tenancies)
      ? getAvailableTenancyCount(tenancies) + 1
      : 4;
  const containerRef = useRef<any>(null);
  const UNAVAILABLE_TENANCIES_TO_SHOW = 1;
  const TENANCIES_TO_SHOW_V2 =
    tenancies?.length - getAvailableTenancyCount(tenancies) === tenancies?.length ? 0 : 2;
  const TENANCIES_TO_SHOW = isRoomTypeV2 ? TENANCIES_TO_SHOW_V2 : TENANCIES_TO_SHOW_V1;
  const [tenanciesToShowCount, setTenanciesToShowCount] = useState(
    isInsideModal ? sortedTenancies?.length : TENANCIES_TO_SHOW,
  );
  const [availableTenancy, unavailableTenancy] = getAvailableTenancy(sortedTenancies);
  const [unavailableTenanciesToShowCount, setUnavailableTenanciesToShowCount] = useState(
    isInsideModal ? unavailableTenancy?.length : UNAVAILABLE_TENANCIES_TO_SHOW,
  );

  const { isHMO, isPartnered, roomTypes, propertyData } = useRoomTypeContext();
  const customWindowOpen = useCustomWindowOpen();
  const { onBookClick } = useBookClick(roomTypeData, customWindowOpen);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateTenanciesToShowCount = () => {
    gtagEventEmitter({
      action:
        sortedTenancies?.length <= tenanciesToShowCount
          ? "view_less_tenancies_clicked"
          : "view_more_tenancies_clicked",
      category: PAGE.INVENTORY,
      label: "success",
    });
    if (isRoomTypeV2 && !isDesktop) {
      setShowMoreDetailsModal(true);
    } else if (sortedTenancies?.length <= tenanciesToShowCount) {
      setTenanciesToShowCount(TENANCIES_TO_SHOW);
    } else if (isRoomTypeV2 && isDesktop) {
      setTenanciesToShowCount(tenancies?.length);
      if (tenancies?.length > 10) {
        if (nextRoomId) propertyPageDispatcher.showNextRoomButton({ state: true, id: nextRoomId });
      }
    } else {
      setTenanciesToShowCount((count) => count + TENANCIES_TO_SHOW);
    }
    if (isDesktop) {
      dispatch(setExpandedRoomType(roomTypeData.id));
    }
  };
  const updateUnavailableTenanciesToShowCount = () => {
    gtagEventEmitter({
      action:
        unavailableTenancy?.length <= unavailableTenanciesToShowCount
          ? "view_less_soldout_tenancies_clicked"
          : "view_more_soldout_tenancies_clicked",
      category: PAGE.INVENTORY,
      label: "success",
    });
    if (isRoomTypeV2 && !isDesktop) {
      setShowMoreDetailsModal(true);
    } else if (unavailableTenancy?.length <= unavailableTenanciesToShowCount) {
      setUnavailableTenanciesToShowCount(UNAVAILABLE_TENANCIES_TO_SHOW);
    } else if (isRoomTypeV2 && isDesktop) {
      setUnavailableTenanciesToShowCount(unavailableTenancy?.length);
    } else {
      setUnavailableTenanciesToShowCount((count) => count + TENANCIES_TO_SHOW);
    }
  };

  const tenancyBookHandler = (tenancy: any, title: string, fromBookNow = false, roomId = 0) => {
    const selectedRoom = roomTypes.data.find((room: any) => room.id === roomTypeData.id);

    try {
      const tenancyBookObj = JSON.parse(JSON.stringify(tenancy));
      tenancyBookObj.tenancy = JSON.parse(JSON.stringify(tenancy));
      tenancyBookObj.config = JSON.parse(JSON.stringify(roomTypeData));
      tenancyBookObj.images = [featuredImage];
      onBookClick(tenancyBookObj, title, fromBookNow, roomId);
    } catch (err) {
      onBookClick(
        { ...tenancy, config: { ...selectedRoom, images: [featuredImage] } },
        title,
        fromBookNow,
        roomId,
      );
    }
  };

  const handleWaitlist = (tenancy = null) => {
    tenancyBookHandler(tenancy, "Join Waitlist");
    gtagEventEmitter({
      action: "Join_waitlist_clicked",
      category: PAGE.INVENTORY,
      label: "success",
      active_tab: activeTab,
    });
  };

  const handleContactUs = () => {
    onBookClick({ ...roomTypeData, images: [featuredImage], resetMoveInDate: true }, "Contact Us");
    gtagEventEmitter({
      action: "contact_us_for_different_dates",
      category: PAGE.INVENTORY,
      label: "success",
      active_tab: activeTab,
    });
  };
  const { showNextRoomButton, nextRoomIdFromStore, expandedRoomType } = useSelector(
    ({ propertyPageData }: AppState) => ({
      showNextRoomButton: propertyPageData.showNextRoomButton,
      nextRoomIdFromStore: propertyPageData.nextRoomId,
      expandedRoomType: propertyPageData.expandedRoomType,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  useEffect(() => {
    setTenanciesToShowCount(
      expandedRoomType === roomTypeData.id ? sortedTenancies?.length : TENANCIES_TO_SHOW,
    );
  }, [expandedRoomType, sortedTenancies?.length, TENANCIES_TO_SHOW]);

  useEffect(() => {
    if (showNextRoomButton && nextRoomIdFromStore) {
      window.addEventListener("scroll", handleModalScroll);
    } else {
      window.removeEventListener("scroll", handleModalScroll);
    }
  }, [showNextRoomButton, nextRoomIdFromStore]);

  const handleModalScroll = throttle(() => {
    if (document.getElementById(nextRoomIdFromStore)) {
      const observer = new IntersectionObserver((entries) => {
        const element = entries[0];
        if (element.isIntersecting) {
          propertyPageDispatcher.showNextRoomButton({ state: false, id: "" });
          observer.unobserve(element.target);
        }
      });

      observer.observe(document.getElementById(nextRoomIdFromStore) as HTMLInputElement);
    }
  }, 300);

  const durationForNoTenancies =
    (isHMO && getLeaseDurationFormatted(propertyData.meta)) || "Flexible";
  const moveInDateForNoTenancies = (isHMO && getAvailableFrom(propertyData.meta)) || "Flexible";
  const moveOutDateForNoTenancies = (isHMO && getAvailableTo(propertyData.meta)) || "";
  const { t } = useTranslationHook();
  const isRootInventoryPricePerPersonEnabled = propertyData?.pricing?.price_per_person || false;
  const isSharedRoom = roomTypeData?.meta?.types?.includes("shared_room");

  const renderTenancyCards = (tenanciesLocal: any[], count: number) =>
    tenanciesLocal.slice(0, count).map((tenancy, idx) => {
      const { isAvailable, duration, moveInDate, moveOutDate, price, floor, facing } =
        formatTenancyDetails({
          ...tenancy,
          pricing: {
            ...tenancy.pricing,
            price_per_person: isSharedRoom && isRootInventoryPricePerPersonEnabled,
          },
        });

      const active_children_tenancy = (
        roomTypeData?.active_children || roomTypeData?.active_children_with_filters
      )?.find((child: any) => child.id === tenancy.id);
      const note = tenancy?.message || active_children_tenancy?.message || "";
      return (
        <TenancyCard
          key={idx}
          name={roomTypeData.name}
          duration={duration}
          moveInDate={moveInDate ?? "Flexible"}
          moveOutDate={moveOutDate}
          price={price}
          tenancy={tenancy}
          isSoldOut={!isAvailable}
          note={note}
          bookHandler={tenancyBookHandler}
          handleWaitlist={handleWaitlist}
          floor={floor}
          facing={facing}
          activeTab={activeTab}
          roomId={roomTypeData.id}
          isInsideModal={isInsideModal}
        />
      );
    });
  const renderContactUsSection = () => {
    if (isInsideModal) {
      return (
        <ContactUsSection
          title={t("propertypage.roomtypesectiondynamic.roomtypecard.looking")}
          CTA={t("propertypage.roomtypesectiondynamic.roomtypecard.contactus")}
          onClick={handleContactUs}
          id={roomTypeData.id}
        />
      );
    }
    if (isRoomTypeV2 && isSoldOut && isPartnered && !isHMO) {
      return (
        <ContactUsSection
          title={t("propertypage.roomtypesectiondynamic.roomtypecard.looking")}
          CTA={t("propertypage.roomtypesectiondynamic.roomtypecard.joinwaitlist")}
          onClick={() => handleWaitlist(roomTypeData)}
          withButton
          id={roomTypeData.id}
        />
      );
    }

    return null;
  };

  const ShowMoreButton = ({
    dataTestId,
    showLessText,
    showMoreText,
    onClick,
    isLess,
    soldOut = false,
  }: {
    dataTestId: string;
    showLessText: string;
    showMoreText: string;
    onClick: () => void;
    isLess: boolean;
    soldOut?: boolean;
  }) => (
    <div
      className={formatClassNames(
        styles.showMoreContainer,
        isDesktop && styles.noBottomBorder,
        soldOut && styles.soldOut,
      )}
    >
      <div />
      <Button
        dataTestId={dataTestId}
        variant="subtle"
        className={formatClassNames(
          styles.showMoreButton,
          isLess && styles.moreTenanciesButton,
          isDesktop && styles.desktop,
          soldOut && styles.soldOut,
        )}
        onClick={onClick}
        RightIcon={ArrowDownIcon}
      >
        {isLess ? showLessText : showMoreText}
      </Button>
    </div>
  );

  return (
    <div
      className={formatClassNames(
        isDesktop ? styles.tenancyContainer_desktop : styles.tenancyContainer,
        isRoomTypeV2 && styles.tenancyContainerV2,
        isInsideModal && styles.insideModal,
      )}
      ref={containerRef}
    >
      {isInsideModal && (
        <h5 className={formatClassNames(styles.title, isDesktop && styles.desktop)}>
          {t("propertypage.roomtypesectiondynamic.roomtypecard.chooseyourtenancy")}
          {/* {sortTenancies?.length > 0 ? `(${sortedTenancies?.length})` : ""} */}
        </h5>
      )}
      {isRoomTypeV2 ? (
        <>
          {renderTenancyCards(
            isInsideModal ? sortedTenancies : availableTenancy,
            isInsideModal ? sortedTenancies?.length : tenanciesToShowCount,
          )}
          {tenanciesToShowCount > TENANCIES_TO_SHOW_V2 && (
            <>
              {!isInsideModal &&
                renderTenancyCards(unavailableTenancy, unavailableTenanciesToShowCount)}
              {unavailableTenancy?.length > 1 && !isInsideModal && (
                <ShowMoreButton
                  dataTestId={
                    unavailableTenancy?.length <= unavailableTenanciesToShowCount
                      ? t("propertypage.roomtypesectiondynamic.roomtypecard.viewless")
                      : `${t("propertypage.roomtypesectiondynamic.roomtypecard.viewmore")}`
                  }
                  showLessText={t("propertypage.roomtypesectiondynamic.roomtypecard.viewless")}
                  showMoreText={`${t(
                    "propertypage.roomtypesectiondynamic.roomtypecard.viewmore",
                  )}(${unavailableTenancy?.length - unavailableTenanciesToShowCount})`}
                  onClick={updateUnavailableTenanciesToShowCount}
                  isLess={unavailableTenancy?.length <= unavailableTenanciesToShowCount}
                  soldOut
                />
              )}
            </>
          )}
        </>
      ) : (
        <>{renderTenancyCards(sortedTenancies, tenanciesToShowCount)}</>
      )}
      <>
        {sortedTenancies?.length === 0 && !isSoldOut && (
          <TenancyCard
            name={roomTypeData.name}
            duration={durationForNoTenancies}
            moveInDate={moveInDateForNoTenancies ?? "Flexible"}
            moveOutDate={moveOutDateForNoTenancies ?? ""}
            price={
              formatTenancyDetails({
                ...roomTypeData,
                pricing: {
                  ...roomTypeData.pricing,
                  price_per_person: (isSharedRoom && isRootInventoryPricePerPersonEnabled) || false,
                },
              })?.price || ""
            }
            isSoldOut={isSoldOut}
            note=""
            bookHandler={tenancyBookHandler}
            handleWaitlist={handleWaitlist}
            floor=""
            facing=""
            activeTab={activeTab}
            roomId={roomTypeData.id}
            roomTypeData={roomTypeData}
            isInsideModal={isInsideModal}
          />
        )}
      </>
      {renderContactUsSection()}
      {!isInsideModal && sortedTenancies?.length > TENANCIES_TO_SHOW && !isSoldOut && (
        <div
          className={formatClassNames(styles.showMoreContainer, isDesktop && styles.noBottomBorder)}
          // onClick={updateTenanciesToShowCount}
          // role="button"
          // tabIndex={0}
          // onKeyDown={(e) => {
          //   if (e.key === "Enter") {
          //     updateTenanciesToShowCount();
          //   }
          // }}
        >
          {isRoomTypeV2 &&
          sortedTenancies?.length <= tenanciesToShowCount &&
          !isHMO &&
          isPartnered &&
          sortedTenancies?.length > 0 &&
          !isSoldOut ? (
            <ContactUsSection
              title={t("propertypage.roomtypesectiondynamic.roomtypecard.looking")}
              CTA={t("propertypage.roomtypesectiondynamic.roomtypecard.contactus")}
              onClick={handleContactUs}
              id={roomTypeData.id}
              className={styles.contactUs}
            />
          ) : (
            <div />
          )}
          <ShowMoreButton
            dataTestId={
              sortedTenancies?.length <= tenanciesToShowCount
                ? t("propertypage.roomtypesectiondynamic.roomtypecard.viewlesstenancies")
                : `${t("propertypage.roomtypesectiondynamic.roomtypecard.viewmoretenancies")}`
            }
            showLessText={t("propertypage.roomtypesectiondynamic.roomtypecard.viewlesstenancies")}
            showMoreText={`${t(
              "propertypage.roomtypesectiondynamic.roomtypecard.viewmoretenancies",
            )} (${tenancies?.length - (availableTenancy?.length > 1 ? 2 : 1)})`}
            onClick={updateTenanciesToShowCount}
            isLess={sortedTenancies?.length <= tenanciesToShowCount}
          />
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(TenanciesSection, {
  componentName: "TenanciesSection-RoomTypeCard-RoomTypeSection-PropertyPage",
  showForChina: true,
});
